type ChatBotContext = {
  chatBotId?: string;
  learnerTrainingId?: string;
  chapterSequenceId?: string | number;
  conversationName?: string;
};

export interface ConversationResponse {
  error?: boolean;
  message?: string;
  conversation_id?: string;
}

export interface MessageResponse {
  error?: boolean;
  message?: string;
  text?: string;
}

export interface ResetResponse {
  error?: boolean;
  message?: string;
  conversation_id?: string;
}

export class ChatBotService {
  private baseUrl = `${process.env.REACT_APP_BACKEND_API_BASE_URL}/api`;
  private baseWebsocketUrl = `${process.env.REACT_APP_BACKEND_WEBSOCKET_BASE_URL}/cable`;
  private token: string;
  private chatBotId?: string;
  private learnerTrainingId?: string;
  private chapterSequenceId?: string | number;
  private conversationName?: string;

  constructor(ctx: ChatBotContext = {}) {
    this.token = this.extractToken();
    this.chatBotId = ctx.chatBotId;
    this.learnerTrainingId = ctx.learnerTrainingId;
    this.chapterSequenceId = ctx.chapterSequenceId;
    this.conversationName = ctx.conversationName;
  }

  private extractToken(): string {
    let token = sessionStorage.getItem('token');

    if (!token) {
      const pathSegments = window.location.pathname.split('/');
      token = pathSegments && pathSegments[2] ? pathSegments[2] : '';
    }

    return token || '';
  }

  async createChatbotConversation(): Promise<ConversationResponse> {
    const url = `${this.baseUrl}/chatbots/conversations`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          error: true,
          message: errorData.errors?.join(' ') || `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        error: true,
        message: 'Network error: Unable to create chatbot conversation.'
      };
    }
  }

  async sendChatbotMessage(
    convId: string,
    txt: string,
    subject?: string
  ): Promise<MessageResponse> {
    const url = `${this.baseUrl}/chatbots/messages`;
    const payload: Record<string, unknown> = {
      conversation_id: convId,
      text: txt,
      bot: 'roleplay_assessor',
      subject
    };

    if (this.chatBotId) payload.chatbot_id = this.chatBotId;
    if (this.learnerTrainingId)
      payload.learner_training_id = this.learnerTrainingId;
    if (this.chapterSequenceId)
      payload.chapter_sequence_id = this.chapterSequenceId;
    if (this.conversationName)
      payload.conversation_name = this.conversationName;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          error: true,
          message: errorData.errors?.join(' ') || `HTTP ${response.status}`
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        error: true,
        message: 'Network error: Unable to send chatbot message.'
      };
    }
  }

  async resetChatbotConversation(): Promise<ResetResponse> {
    const url = `${this.baseUrl}/chatbots/reset`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          error: true,
          message: errorData.errors?.join(' ') || `HTTP ${response.status}`
        };
      }

      return response.json();
    } catch (error) {
      return {
        error: true,
        message: 'Network error: Unable to reset chatbot conversation.'
      };
    }
  }

  getWebSocketUrl(): string {
    return `${this.baseWebsocketUrl}?token=${encodeURIComponent(this.token)}`;
  }

  getChatBotId(): string | undefined {
    return this.chatBotId || undefined;
  }

  getLearnerTrainingId(): string | undefined {
    return this.learnerTrainingId;
  }

  getChapterSequenceId(): string | number | undefined {
    return this.chapterSequenceId;
  }

  getConversationName(): string | undefined {
    return this.conversationName;
  }
}
